import { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import { Autocomplete, FormControlLabel, Switch, TextField, TextFieldProps } from "@mui/material";

import AedasLoading from "../../Components/Shared/AedasLoading/AedasLoading";
import CustomModal from "../../Components/Shared/ModalCustom/CustomModal";
import { addEmployee, getEmployee, updateEmployee } from "../../Service/EmployeeService/EmployeeService";
import { apiErrorStatus, handleApiError } from "../../Utils/apiErrors";
import { isActive } from "./userUtils";

// Alta (POST /employee) y edición (PUT /employee/{id}) de un usuario.
// Al editar solo se envían los campos que cambian.

type FormValues = {
  name: string;
  surname: string;
  display_name: string;
  email: string;
  telephone: string;
  id_rol: number | null;
  id_azure: string;
  azure_rol: string;
  delegacion_provincial: string;
  url_img: string;
  active: boolean;
};

const EMPTY: FormValues = {
  name: "",
  surname: "",
  display_name: "",
  email: "",
  telephone: "",
  id_rol: null,
  id_azure: "",
  azure_rol: "",
  delegacion_provincial: "",
  url_img: "",
  active: true,
};

const asText = (value: any) => (value === null || value === undefined ? "" : String(value));

const toFormValues = (employee: any): FormValues => ({
  name: asText(employee.name),
  surname: asText(employee.surname),
  display_name: asText(employee.display_name),
  email: asText(employee.email),
  telephone: asText(employee.telephone),
  id_rol: employee.id_rol ?? null,
  id_azure: asText(employee.id_azure),
  azure_rol: asText(employee.azure_rol),
  delegacion_provincial: asText(employee.delegacion_provincial),
  url_img: asText(employee.url_img),
  active: isActive(employee),
});

// name, email, id_rol y active nunca se envían como null; el resto, vacío = null
const toPayload = (values: FormValues): Record<string, any> => {
  const optional = (value: string) => (value.trim() === "" ? null : value.trim());
  return {
    name: values.name.trim(),
    email: values.email.trim(),
    id_rol: values.id_rol,
    active: values.active,
    surname: optional(values.surname),
    display_name: optional(values.display_name),
    telephone: optional(values.telephone),
    id_azure: optional(values.id_azure),
    azure_rol: optional(values.azure_rol),
    url_img: optional(values.url_img),
    delegacion_provincial: values.delegacion_provincial.trim() === "" ? null : Number(values.delegacion_provincial),
  };
};

const changedFields = (before: Record<string, any>, after: Record<string, any>) =>
  Object.fromEntries(Object.entries(after).filter(([key, value]) => before[key] !== value));

// Al crear se omiten los vacíos (display_name lo rellena el back con "nombre apellidos")
const withoutEmpty = (payload: Record<string, any>) =>
  Object.fromEntries(Object.entries(payload).filter(([, value]) => value !== null));

const REQUIRED = "Este campo es requerido";

const validationSchema = yup.object({
  name: yup.string().trim().required(REQUIRED),
  email: yup.string().trim().email("Introduzca un email válido").required(REQUIRED),
  id_rol: yup.number().nullable().required(REQUIRED),
  delegacion_provincial: yup.string().matches(/^\d*$/, "Debe ser un número"),
});

type UserFormModalProps = {
  employeeId: number | null; // null = alta
  roles: any[] | null;
  selfId?: number;
  onClose: () => void;
  onChanged: (id?: number) => void; // recargar tabla y estadísticas
};

export default function UserFormModal(props: UserFormModalProps) {
  const { employeeId, roles, selfId, onClose, onChanged } = props;
  const isEdit = employeeId !== null;
  const isSelf = isEdit && employeeId === selfId;

  const [initial, setInitial] = useState<FormValues>(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (employeeId === null) return;
    getEmployee(employeeId)
      .then((response) => setInitial(toFormValues(response.data)))
      .catch((error) => {
        handleApiError(error, { fallback: "No se ha podido cargar el usuario" });
        if (apiErrorStatus(error) === 404) onChanged();
        onClose();
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  const formik = useFormik<FormValues>({
    initialValues: initial,
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, { setErrors }) => {
      const payload = toPayload(values);
      const request = isEdit ? changedFields(toPayload(initial), payload) : withoutEmpty(payload);
      if (isEdit && Object.keys(request).length === 0) {
        toast.info("No hay cambios que guardar");
        onClose();
        return;
      }
      setSaving(true);
      (isEdit ? updateEmployee(employeeId!, request) : addEmployee(request))
        .then((response) => {
          toast.success(isEdit ? "Usuario actualizado" : "Usuario creado");
          onChanged(response.data?.id ?? employeeId ?? undefined);
          onClose();
        })
        .catch((error) =>
          handleApiError(error, {
            fallback: "No se ha podido guardar el usuario",
            setFieldErrors: (errors) => setErrors(errors as any),
            onNotFound: () => {
              onChanged();
              onClose();
            },
          })
        )
        .finally(() => setSaving(false));
    },
  });

  const errorOf = (name: keyof FormValues) =>
    formik.errors[name] && (formik.touched[name] || formik.submitCount > 0) ? String(formik.errors[name]) : null;

  const textField = (name: keyof FormValues, label: string, options: TextFieldProps = {}) => (
    <TextField
      {...options}
      id={`user-${name}`}
      name={name}
      label={label}
      value={formik.values[name]}
      onChange={formik.handleChange}
      onBlur={formik.handleBlur}
      error={Boolean(errorOf(name))}
      helperText={errorOf(name) ?? options.helperText}
    />
  );

  const roleOptions = roles ?? [];

  const body = loading ? (
    <AedasLoading />
  ) : (
    <form className="aedasForm" onSubmit={formik.handleSubmit} noValidate>
      {textField("name", "Nombre", { required: true })}
      {textField("surname", "Apellidos")}
      {textField("email", "Email", { required: true, type: "email" })}
      {textField("display_name", "Nombre visible", { helperText: "Si se deja vacío: nombre y apellidos" })}
      <Autocomplete
        id="user-id_rol"
        options={roleOptions}
        getOptionLabel={(option: any) => option.name}
        isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
        value={roleOptions.find((rol: any) => rol.id === formik.values.id_rol) ?? null}
        onChange={(_e, value: any) => formik.setFieldValue("id_rol", value?.id ?? null)}
        onBlur={() => formik.setFieldTouched("id_rol")}
        disabled={isSelf}
        noOptionsText="Sin opciones"
        renderInput={(params) => (
          <TextField
            {...params}
            required
            label="Rol"
            error={Boolean(errorOf("id_rol"))}
            helperText={errorOf("id_rol") ?? (isSelf ? "No puedes cambiar tu propio rol" : undefined)}
          />
        )}
      />
      {textField("telephone", "Teléfono", { type: "tel" })}
      {textField("id_azure", "Id de Azure AD")}
      {textField("azure_rol", "Rol de Azure")}
      {textField("delegacion_provincial", "Delegación provincial", { inputProps: { inputMode: "numeric" } })}
      {textField("url_img", "URL de la imagen")}
      <FormControlLabel
        className="aedasForm__full"
        control={
          <Switch
            id="user-active"
            color="success"
            checked={formik.values.active}
            disabled={isSelf}
            onChange={(e) => formik.setFieldValue("active", e.target.checked)}
          />
        }
        label={isSelf ? "Usuario activo (no puedes desactivarte)" : "Usuario activo"}
      />
      {/* Enviar con Intro */}
      <button type="submit" hidden />
    </form>
  );

  return (
    <CustomModal
      size="lg"
      show
      onChange={onClose}
      title={isEdit ? "Editar usuario" : "Añadir usuario"}
      body={body}
      buttons={[
        { content: "Cancelar", primary: false, action: onClose },
        { content: "Guardar", primary: true, action: formik.submitForm, disabled: loading || saving },
      ]}
    />
  );
}
